import React, {
  useMemo, useEffect, useCallback, useState,
} from 'react';
import {
  Input,
  Dropdown,
} from 'semantic-ui-react';
import util from 'utils/utils';
import {
  useAppSelector, useAppDispatch,
} from 'store';
import {
  Other,
} from 'simplydo/interfaces';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import api from 'api';
import actions from 'actions';
import ProductTour from 'components/lib/ProductTour';
import CircularButtonWithNumber from './CircularButtonWithNumber';
import { StyledNavBarSubSegment } from './UI/NavBar';
import useThrottle from 'utils/useThrottle';

const StyledDropdown = styled(Dropdown)`
  .menu.visible {
    right: -6px !important;
    margin: 0.75em 0 0 !important;
    width: 300px;
  }
`;

const StyledDropdownItem = styled(Dropdown.Item)<{ clickable?: boolean }>`
  ${({ clickable }) => (clickable ? `
    cursor: pointer;
    &:hover {
      background-color: #e9ebee;
    }
  ` : '')}
  img {
    height: 30px;
    width: 30px !important;
    object-fit: contain;
  }
`;

const OrgListItem = ({
  organisation,
  onClick,
  showCode,
}: {
  organisation: Other.IOrganisation,
  onClick?: (event: React.MouseEvent<HTMLElement>) => void,
  showCode?: boolean,
}) => (
  <StyledDropdownItem
    clickable={!!onClick}
    onClick={onClick}
    image={organisation.darkLogoUrl}
    content={organisation.name}
    description={showCode ? organisation.code : null}
  />
);

const OrgSwitcherTray = ({
  style,
} : {
  style?: React.CSSProperties,
}) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgs, setOrgs] = useState<Other.IOrganisation[]>([]);
  const [superadminOrgSelectorVisible, setSuperadminOrgSelectorVisible] = useState(true);
  const dispatch = useAppDispatch();

  const user: Other.IUserMe & { availableOrganisations?: Other.IOrganisation[] } = useAppSelector((state) => state.user);

  const showOrgSwitcherTray = useMemo(() => (
    util.isInRole(user, 'super.viewDashboard')
    || (
      user?.availableOrganisations?.length
      && user?.availableOrganisations?.length > 1
    )
  ), [user]);

  const fetchOrganisations = useThrottle(() => {
    setOrgLoading(true);
    api.organisations.getAll({
      page: 1, query: orgSearch, selectedTypes: [], selectedTags: [],
    }, (data) => {
      setOrgs(data.organisations);
      setOrgLoading(false);
    }, (err) => {
      toast.error(err.message);
      setOrgLoading(false);
    });
  }, 400, [orgSearch]);

  const switchOrganisation = useCallback((orgId) => {
    api.users.switchOrganisation(user?._id, orgId, (data) => {
      dispatch(actions.user.switchOrganisation(data.organisation));
    }, (err) => {
      toast.error(err.message);
    });
  }, [dispatch, user?._id]);

  useEffect(() => {
    if (popupOpen) {
      fetchOrganisations();
    }
  }, [fetchOrganisations, popupOpen]);

  if (!user) {
    return null;
  }
  if (!showOrgSwitcherTray) {
    return null;
  }
  return (
    <>
      <ProductTour
        tour='orgSwitcher'
        isReady={!!user?.availableOrganisations?.length}
      />
      <StyledNavBarSubSegment>
        <StyledDropdown
          on='click'
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          icon={null}
          pointing='top right'
          closeOnChange={false}
          trigger={(
            <div onClick={() => setPopupOpen(true)}>
              <CircularButtonWithNumber
                image={util.orgFavicon(user.organisation)}
                count={user?.availableOrganisations?.length}
                style={style}
                labelProps={{
                  color: 'grey',
                }}
                className='org-switcher-tour-step-1'
                square
              />
            </div>
          )}
        >
          <Dropdown.Menu>
            <Dropdown.Header content='Current Organisation' />
            <OrgListItem organisation={user.organisation} showCode />
            <Dropdown.Header content='Switch Organisation' />
            {user?.availableOrganisations?.length
              ? (
                <>
                  {user.availableOrganisations.filter((organisation) => organisation._id !== user.organisation._id).map((organisation) => (
                    <OrgListItem
                      key={organisation._id}
                      organisation={organisation}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        switchOrganisation(organisation._id);
                      }}
                    />
                  ))}
                </>
              ) : null
            }
            {(user?.availableOrganisations?.length && util.isInRole(user, 'super.viewDashboard') && superadminOrgSelectorVisible)
              ? (
                <Dropdown.Divider />
              ) : null
            }
            {util.isInRole(user, 'super.viewDashboard') && superadminOrgSelectorVisible
              ? (
                <>
                  <Dropdown.Header content='Superadmin Org Selector' />
                  <Input
                    value={orgSearch}
                    onChange={(e, { value }) => setOrgSearch(value)}
                    autoFocus
                    placeholder='Search for org'
                    onFocus={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    loading={orgLoading}
                  />
                  {orgs.map((organisation) => (
                    <OrgListItem
                      key={organisation._id}
                      organisation={organisation}
                      showCode
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        switchOrganisation(organisation._id);
                      }}
                    />
                  ))}
                  <Dropdown.Item
                    content='Hide superadmin orgs this session'
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSuperadminOrgSelectorVisible(false);
                    }}
                  />
                </>
              ) : null
            }
          </Dropdown.Menu>
        </StyledDropdown>
      </StyledNavBarSubSegment>
    </>
  );
};

export default OrgSwitcherTray;
